<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar>
      </ion-toolbar>
    </ion-header>

    <ion-content :fullscreen="true">

      <div style="display: flex; justify-content: flex-end; padding: 8px 16px; align-items: center;">
                <ion-icon :icon="globeOutline"></ion-icon>
                <ion-select style="width: auto; max-width: 160px;"
                  :value="languageInUse"
                  justify="start"
                  @ionChange="handleChange($event,'language')"
                  @ionCancel="handleCancel()"
                  @ionDismiss="handleDismiss()">
                  <div v-for="item in languages">
                    <ion-select-option :value="item.radio">{{ item.name }}</ion-select-option>
                  </div>
                </ion-select>
      </div>

      <!-- Hero -->
      <section class="hero">
        <img src="/assets/png/chess_square_400.png" alt="ChessVis" class="hero-logo-img" />
        <h1 class="hero-title">Chess<em>Vis</em></h1>
        <p class="hero-tagline">{{ t("Train smarter. Prepare deeper. Play better.") }}</p>
        <p class="hero-sub">{{ t("The only chess app built around visualization training and data-driven opening preparation — with an AI coach to close the loop on your games.") }}</p>
        <ion-button expand="block" class="hero-cta" 
                    @click="onClick('home_menu',t('Home Menu'),'program')">
          {{ t("Start Free — No Account Needed") }}
        </ion-button>
      </section>

      <!-- Stores  -->
      <p class="also-available">{{ t("Also available on") }}</p>
      <div class="store-buttons">
        <ion-button fill="outline" class="store-btn" @click="handleAppStore">
          <ion-icon :icon="logoApple" slot="start" />
          App Store
        </ion-button>
        <ion-button fill="outline" class="store-btn" @click="handleGooglePlay">
          <ion-icon :icon="logoGooglePlaystore" slot="start" />
          Google Play
        </ion-button>
      </div>
      <p class="install-note">
        {{ t("Tip: Install as a web app anytime via Account → Support") }}
      </p>

      <!-- Origin story -->
      <section class="origin-section">
        <p class="origin-text">{{ t("ChessVis started as a visualization app — it's in the name. While other apps were adding more puzzles, we were asking a harder question: <em>what does it actually take to see the board better?</em> That obsession never went away. It just grew into something bigger.") }}</p>
      </section>

      <!-- The journey -->
      <section class="journey-section">
        <h2 class="journey-title">{{ t("One app. A complete training loop.") }}</h2>

        <!-- Step 1: Tactics Gym -->
        <div class="journey-card teal-card">
          <div class="journey-step">
            <div class="step-icon-wrap teal">
              <ion-icon :icon="barbellOutline" aria-hidden="true" />
            </div>
            <div class="step-label teal-label">{{ t("Step 1 · Puzzles & Visualization - Always Free") }}</div>
          </div>
          <h3 class="journey-card-title">{{ t("Train in the Tactics Gym") }}</h3>
          <p class="journey-card-body">{{ t("Come for the puzzles and visualization. 4.5 million rated puzzles, unique visualized puzzle training, blindfold chess with stepping stones, and board visualization tools no other app has built. The kind of training that makes patterns click.") }}</p>
          <div class="journey-features">
            <div class="journey-feature">
              <ion-icon :icon="checkmarkOutline" class="check teal-check" aria-hidden="true" />
              <span>{{ t("4.5M+ puzzles, never repeated") }}</span>
            </div>
            <div class="journey-feature">
              <ion-icon :icon="checkmarkOutline" class="check teal-check" aria-hidden="true" />
              <span>{{ t("Visualized puzzles — unique to ChessVis") }}</span>
            </div>
            <div class="journey-feature">
              <ion-icon :icon="checkmarkOutline" class="check teal-check" aria-hidden="true" />
              <span>{{ t("Who's Guarding Whom — unique to ChessVis") }}</span>
            </div>
            <div class="journey-feature">
              <ion-icon :icon="checkmarkOutline" class="check teal-check" aria-hidden="true" />
              <span>{{ t("Blindfold chess with stepping-stone onboarding") }}</span>
            </div>
          </div>
          <div class="journey-cta-wrap">
            <ion-button fill="outline" class="journey-cta teal-cta" 
                          @click="onClick('tactics_gym_landing_page',t('Tactics Gym'),'program')">
              {{ t("Explore Tactics Gym") }}
            </ion-button>
          </div>
        </div>

        <!-- Step 2: Studio -->
        <div class="journey-card purple-card">
          <div class="journey-step">
            <div class="step-icon-wrap purple">
              <ion-icon :icon="libraryOutline" aria-hidden="true" />
            </div>
            <div class="step-label purple-label">{{ t("Step 2 · Studio") }}</div>
          </div>
          <h3 class="journey-card-title">{{ t("Stay for the Opening Preparation") }}</h3>
          <p class="journey-card-body">{{ t("Most players study openings wrong — memorizing long lines they'll rarely see, against players who don't play like them. ChessVis Opening Mastery is built on 200+ million real games at your rating level, filtered to head-to-head peers. Study the moves that actually matter.") }}</p>
          <div class="journey-features">
            <div class="journey-feature">
              <ion-icon :icon="checkmarkOutline" class="check purple-check" aria-hidden="true" />
              <span>{{ t("Peer-level games — study players like you") }}</span>
            </div>
            <div class="journey-feature">
              <ion-icon :icon="checkmarkOutline" class="check purple-check" aria-hidden="true" />
              <span>{{ t("Middle game impact — unique to ChessVis") }}</span>
            </div>
            <div class="journey-feature">
              <ion-icon :icon="checkmarkOutline" class="check purple-check" aria-hidden="true" />
              <span>{{ t("AI recommends which moves to play — and which to avoid") }}</span>
            </div>
            <div class="journey-feature">
              <ion-icon :icon="checkmarkOutline" class="check purple-check" aria-hidden="true" />
              <span>{{ t("Repertoire builder with spaced repetition drill") }}</span>
            </div>
            <div class="journey-feature">
              <ion-icon :icon="checkmarkOutline" class="check purple-check" aria-hidden="true" />
              <span>{{ t("Practice into the middle game — find your weak branches") }}</span>
            </div>
          </div>
          <div class="journey-cta-wrap">
            <ion-button fill="outline" class="journey-cta purple-cta" 
                            @click="onClick('studio_landing_page',t('Studio'),'program')">
              {{ t("Explore Studio") }}
            </ion-button>
          </div>
        </div>

        <!-- Step 3: AI -->
        <div class="journey-card ai-card">
          <div class="journey-step">
            <div class="step-icon-wrap ai">
              <ion-icon :icon="sparklesOutline" aria-hidden="true" />
            </div>
            <div class="step-label ai-label">{{ t("Step 3 · ChessVis AI") }}</div>
          </div>
          <h3 class="journey-card-title">{{ t("Review Your Games with an AI Coach") }}</h3>
          <p class="journey-card-body">{{ t("You've prepared your openings. You've played your games. Now, venture over to ChessVis AI and understand what actually happened. ChessVis AI closes the loop — analyzing your games in plain language, flagging where your preparation held up and where it broke down.") }}</p>
          <div class="journey-features">
            <div class="journey-feature">
              <ion-icon :icon="checkmarkOutline" class="check ai-check" aria-hidden="true" />
              <span>{{ t("Deep game analysis in plain language") }}</span>
            </div>
            <div class="journey-feature">
              <ion-icon :icon="checkmarkOutline" class="check ai-check" aria-hidden="true" />
              <span>{{ t("Turning points and critical moments flagged") }}</span>
            </div>
            <div class="journey-feature">
              <ion-icon :icon="checkmarkOutline" class="check ai-check" aria-hidden="true" />
              <!-- <span>{{ t("Monthly credits included with subscription") }}</span> -->
              <span>{{ t("Analyzed for pennies per game") }}</span>
            </div>
            <div class="journey-feature">
              <ion-icon :icon="checkmarkOutline" class="check ai-check" aria-hidden="true" />
              <span>{{ t("Access Chessvis AI while staying in this program") }}</span>
            </div>
          </div>
        </div>

      </section>

      <!-- Confidence section -->
      <section class="confidence-section">
        <h2 class="confidence-title">{{ t("Why ChessVis?") }}</h2>

        <div class="confidence-card">
          <h3>{{ t("Built different from the start") }}</h3>
          <p>{{ t("ChessVis didn't start by copying other chess apps. It started by asking what visualization training should actually look like — and built the tools to answer that question. No other app has Visualized Puzzles or Who's Guarding Whom because no other app started from the same place.") }}</p>
        </div>

        <div class="confidence-card">
          <h3>{{ t("Real data, real rating, real peers") }}</h3>
          <p>{{ t("Studying the masters is a nice theory. In reality, they don't play like us. ChessVis Opening Mastery is the only tool that filters opening data specifically to your rating bracket — head-to-head games against players within 50 points of you. That's the data that matters.") }}</p>
        </div>

        <div class="confidence-card">
          <h3>{{ t("A complete loop, not a collection of features") }}</h3>
          <p>{{ t("Train your pattern recognition in the Tactics Gym. Build your opening preparation in Studio. Review your actual games with the AI coach. Each part feeds the next. That's not an accident — it's the point.") }}</p>
        </div>
      </section>

      <!-- Final CTA -->
      <section class="final-cta-section">
        <h2 class="final-cta-title">{{ t("Start where you are.") }}</h2>
        <p class="final-cta-sub">{{ t("Tactics Gym puzzles & visualation exercises are completely free. No account, no commitment — just better chess training, right now.") }}</p>
        <ion-button expand="block" class="final-cta-btn" 
                      @click="onClick('home_menu',t('Home Menu'),'program')">
          {{ t("Start Free") }}
        </ion-button>
        <ion-button expand="block" fill="outline" class="final-cta-secondary" 
                        @click="onClick('subscribe_blurb',t('Subscriber Benefits'),'you')">
          {{ t("View Subscription Options") }}
        </ion-button>
      </section>

      <!-- Stores  -->
      <p class="also-available">{{ t("Also available on") }}</p>
      <div class="store-buttons">
        <ion-button fill="outline" class="store-btn" @click="handleAppStore">
          <ion-icon :icon="logoApple" slot="start" />
          App Store
        </ion-button>
        <ion-button fill="outline" class="store-btn" @click="handleGooglePlay">
          <ion-icon :icon="logoGooglePlaystore" slot="start" />
          Google Play
        </ion-button>
      </div>
      <p class="install-note">
        {{ t("Tip: Install as a web app anytime via Account → Support") }}
      </p>

    </ion-content>

    <BottomNav @bottom-click="onClick"/>
  </ion-page>
</template>

<script setup lang="ts">
import {
  IonPage, IonHeader, IonToolbar, IonTitle,
  IonContent, IonIcon, IonButton,
  IonSelect,IonSelectOption, IonItem
} from '@ionic/vue'
import {
  eyeOutline, barbellOutline, libraryOutline,
  sparklesOutline, checkmarkOutline, globeOutline
} from 'ionicons/icons'
import { logoApple, logoAppleAppstore,logoGooglePlaystore } from 'ionicons/icons';

import BottomNav from "@/components/BottomNav.vue";
import { useConnectionStore } from '@/stores/connectionStore';
import { t, setTranslationLanguage } from '@/composables/translateText.js'
import { ref, watch } from 'vue';

const emit = defineEmits(['menu-item-click'])

const connectionStore = useConnectionStore()
const languageInUse = ref(connectionStore.getLanguageInUse)

const languages = [
      { name :  t("English"), radio : "en"},
      { name :  t("Spanish"), radio : "es"},
      { name :  t("French"), radio : "fr"},
      { name :  t("German"), radio : "de"},
      { name :  t("Italian"), radio : "it"},
      { name :  t("Portuguese"), radio : "pt"},
      { name :  t("Swedish"), radio : "sv"},
    ]

function handleChange(ev,which) {
    //console.log('ionChange fired with value: ' + ev.detail.value,which);

    switch (which) {
      case "language" :
        // if language changed, reload page
        if (languageInUse.value == ev.detail.value)
          break
        connectionStore.setLanguageIncludingSystem(ev.detail.value)
        languageInUse.value = ev.detail.value
        setTranslationLanguage(languageInUse.value)
        //translateText()
        window.location.reload(); // Or simply location.reload();
        break
  
    }
   
  }

  function handleCancel() {
    //console.log('ionCancel fired');
  }
  function handleDismiss() {
    //console.log('ionDismiss fired');
  }

function onClick(path : string,label : string,whichMenu : string) {
  //console.log("Landing page CLICK ",path,label)
  connectionStore.setSeenNewLandingPage(true)
  emit("menu-item-click",path,label,whichMenu)
}

function handleAppStore() {
  const urlDestination = "https://itunes.apple.com/us/app/chessvis/id1229226444"
  window.open(urlDestination, '_blank');
}

function handleGooglePlay() {
  const urlDestination = "https://play.google.com/store/apps/details?id=com.conceptual.chessvis"
  window.open(urlDestination, '_blank');
}

</script>

<style scoped>

ion-select {
  --background: transparent;
}

ion-toolbar {
  --background: #12122a;
  --color: #ffffff;
  --border-color: #2a2a4a;
}

ion-title {
  font-size: 17px;
  font-weight: 500;
}

ion-content {
  --background: #1a1a2e;
}

/* ── Hero ── */
.hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 24px 32px;
  border-bottom: 0.5px solid #2a2a4a;
}

.hero-logo {
  width: 72px;
  height: 72px;
  border-radius: 20px;
  background: linear-gradient(135deg, #085041, #3C3489);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
}

/* Replace .hero-logo and .hero-logo-icon with: */
.hero-logo-img {
  width: 100px;
  height: 100px;
  border-radius: 16px;
  margin-bottom: 18px;
}

.hero-title {
  font-size: 32px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 10px;
  letter-spacing: -0.5px;
}

.hero-title em {
  font-style: normal;
  color: #9FE1CB;
}

.hero-tagline {
  font-size: 16px;
  font-weight: 500;
  color: #AFA9EC;
  margin: 0 0 12px;
  line-height: 1.4;
}

.hero-sub {
  font-size: 14px;
  color: #888880;
  line-height: 1.6;
  margin: 0 0 24px;
}

.hero-cta {
  --background: #1D9E75;
  --background-activated: #168a64;
  --color: #ffffff;
  --border-radius: 14px;
  font-size: 15px;
  font-weight: 600;
  height: 52px;
  width: 100%;
}

/* ── Origin ── */
.origin-section {
  padding: 24px 20px;
  border-bottom: 0.5px solid #2a2a4a;
  background: #0f0f22;
}

.origin-text {
  font-size: 14px;
  color: #aaa8c0;
  line-height: 1.7;
  margin: 0;
  font-style: italic;
}

.origin-text em {
  color: #9FE1CB;
  font-style: normal;
  font-weight: 500;
}

/* ── Journey ── */
.journey-section {
  padding: 24px 16px 16px;
  border-bottom: 0.5px solid #2a2a4a;
}

.journey-title {
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 18px;
  text-align: center;
}

.journey-card {
  border-radius: 16px;
  padding: 20px 16px;
  margin-bottom: 14px;
  border: 0.5px solid #2a2a4a;
}

.teal-card  { background: #0a1f19; border-color: #1D9E75; }
.purple-card { background: #130f2e; border-color: #534AB7; }
.ai-card    { background: #1a1020; border-color: #7F77DD; }

.journey-step {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.step-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.step-icon-wrap.teal   { background: #085041; color: #9FE1CB; }
.step-icon-wrap.purple { background: #3C3489; color: #CECBF6; }
.step-icon-wrap.ai     { background: #2d2060; color: #c4bfff; }

.step-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.teal-label   { color: #1D9E75; }
.purple-label { color: #7F77DD; }
.ai-label     { color: #AFA9EC; }

.journey-card-title {
  font-size: 17px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 8px;
}

.journey-card-body {
  font-size: 13px;
  color: #888880;
  line-height: 1.6;
  margin: 0 0 14px;
}

.journey-features {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.journey-feature {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #c8c6d8;
  line-height: 1.4;
}

.check {
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 1px;
}

.teal-check   { color: #1D9E75; }
.purple-check { color: #7F77DD; }
.ai-check     { color: #AFA9EC; }

.journey-cta-wrap { margin-top: 4px; }

.journey-cta {
  --border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  height: 40px;
}

.teal-cta   { --color: #1D9E75; --border-color: #1D9E75; }
.purple-cta { --color: #7F77DD; --border-color: #7F77DD; }

/* ── Confidence ── */
.confidence-section {
  padding: 24px 16px 8px;
  border-bottom: 0.5px solid #2a2a4a;
}

.confidence-title {
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 16px;
  text-align: center;
}

.confidence-card {
  background: #1e1e35;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 10px;
  border: 0.5px solid #2a2a4a;
}

.confidence-card h3 {
  font-size: 15px;
  font-weight: 600;
  color: #e0e0f0;
  margin: 0 0 6px;
}

.confidence-card p {
  font-size: 13px;
  color: #b0b0bc;
  line-height: 1.6;
  margin: 0;
}

/* ── Final CTA ── */
.final-cta-section {
  padding: 32px 20px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
}

.final-cta-title {
  font-size: 22px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
}

.final-cta-sub {
  font-size: 14px;
  color: #888880;
  line-height: 1.6;
  margin: 0;
}

.final-cta-btn {
  --background: #1D9E75;
  --background-activated: #168a64;
  --color: #ffffff;
  --border-radius: 14px;
  font-size: 15px;
  font-weight: 600;
  height: 52px;
  width: 100%;
  margin-top: 8px;
}

.final-cta-secondary {
  --color: #AFA9EC;
  --border-color: #534AB7;
  --border-radius: 14px;
  font-size: 14px;
  height: 48px;
  width: 100%;
}

.also-available {
  font-size: 13px;
  color: #888880;
  margin: 0px 0 10px;
  text-align: center;
}

.store-buttons {
  display: flex;
  gap: 10px;
  width: 100%;
  justify-content: center;
  margin-bottom: 12px;
}

.store-btn {
  --color: #CECBF6;
  --border-color: #7F77DD;
  --border-radius: 10px;
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  height: 44px;
}

.install-web {
  font-size: 12px;
  color: #7F77DD;
  margin: 4px 0 0;
  text-align: center;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.install-note {
  font-size: 13px;
  color: #888880;
  margin: 8px 0 0;
  text-align: center;
  font-style: italic;
}


</style>
